/**
 * ClaritySettingsCard.jsx
 *
 * Lets an author connect their own Microsoft Clarity project so heatmaps
 * and session recordings fire on their book pages (Free Sample + Landing Page)
 * only — completely isolated from every other author's traffic.
 *
 * Backend: PUT /api/auth/me/analytics  { clarity_project_id: string }
 *          GET /api/auth/me            → { clarity_project_id: string }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MousePointerClick, CheckCircle2, ExternalLink, Loader2,
  ChevronDown, ChevronUp, Trash2, Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Matches the backend regex: ^[a-zA-Z0-9_-]{1,30}$
const CLARITY_RE = /^[a-zA-Z0-9_-]{1,30}$/;

const STEPS = [
  {
    n: 1,
    title: 'Create a free Clarity account',
    body: (
      <>
        Go to{' '}
        <a
          href="https://clarity.microsoft.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary hover:underline"
        >
          clarity.microsoft.com <ExternalLink className="h-2.5 w-2.5" />
        </a>{' '}
        and sign in with a Microsoft, Google, or Facebook account. Clarity is
        completely free with no traffic limits.
      </>
    ),
  },
  {
    n: 2,
    title: 'Create a new project',
    body: (
      <>
        Click <b>+ Add new project</b>, give it any name (e.g. "My Books"),
        then choose <b>Other</b> as the installation method when asked. You
        do not need to paste any script — The Loom handles that automatically.
      </>
    ),
  },
  {
    n: 3,
    title: 'Copy your Project ID',
    body: (
      <>
        After the project is created you'll land on a setup page. The{' '}
        <b>Project ID</b> is the short code in the tracking snippet that looks
        like <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10.5px]">abc123de</code>. Copy just that code (no quotes, no angle brackets) and paste it below.
      </>
    ),
  },
];

export default function ClaritySettingsCard() {
  const [current, setCurrent]   = useState('');   // ID saved in DB
  const [draft, setDraft]       = useState('');   // what the user is typing
  const [saving, setSaving]     = useState(false);
  const [clearing, setClearing] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [showSteps, setShowSteps] = useState(false);

  // Load existing value from /api/auth/me
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        const id = r.data?.clarity_project_id || '';
        setCurrent(id);
        setDraft(id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isActive = CLARITY_RE.test(current);
  const draftValid = draft.trim() === '' || CLARITY_RE.test(draft.trim());
  const draftChanged = draft.trim() !== current;
  const canSave = draftValid && draftChanged && draft.trim() !== '';

  const save = async () => {
    const value = draft.trim();
    if (!CLARITY_RE.test(value)) {
      toast.error('Project ID must be 1–30 letters, digits, underscores or hyphens.');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/me/analytics`,
        { clarity_project_id: value },
        { withCredentials: true },
      );
      setCurrent(value);
      toast.success('Clarity Project ID saved. Heatmaps will fire on your book pages.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save Clarity ID.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setClearing(true);
    try {
      await axios.put(
        `${API}/auth/me/analytics`,
        { clarity_project_id: '' },
        { withCredentials: true },
      );
      setCurrent('');
      setDraft('');
      toast.success('Clarity Project ID removed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear Clarity ID.');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-[12px] text-muted-foreground"
           data-testid="clarity-card-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading analytics settings…
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border p-3 transition-colors ${
        isActive
          ? 'border-blue-200 bg-blue-50/40 dark:border-blue-900/50 dark:bg-blue-950/20'
          : 'border-border bg-card'
      }`}
      data-testid="clarity-settings-card"
    >
      {/* ── Header ── */}
      <div className="flex items-start gap-3">
        <MousePointerClick className={`mt-0.5 h-5 w-5 shrink-0 ${isActive ? 'text-blue-600' : 'text-muted-foreground'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-[13px] font-semibold text-foreground">
              Microsoft Clarity — Heatmaps &amp; Session Recordings
            </h4>
            {isActive && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800 dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                data-testid="clarity-active-badge"
              >
                <CheckCircle2 className="h-2.5 w-2.5" /> Tracking active
              </span>
            )}
          </div>

          <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
            Connect your own free Clarity account to see exactly how readers interact
            with your <b>Free Sample</b> and <b>Book Landing</b> pages — scroll depth,
            rage clicks, session replays. Your Project ID tracks <em>only your books</em>;
            no other author's pages are ever included.
          </p>

          {/* ── Setup instructions accordion ── */}
          <button
            type="button"
            onClick={() => setShowSteps((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
            data-testid="clarity-steps-toggle"
          >
            {showSteps ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showSteps ? 'Hide setup guide' : 'How to get your Project ID'}
          </button>

          {showSteps && (
            <ol className="mt-2 space-y-2.5" data-testid="clarity-steps-list">
              {STEPS.map((s) => (
                <li key={s.n} className="flex gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                    {s.n}
                  </span>
                  <div className="text-[11.5px] leading-relaxed text-muted-foreground">
                    <b className="text-foreground">{s.title}. </b>
                    {s.body}
                  </div>
                </li>
              ))}
            </ol>
          )}

          {/* ── Input row ── */}
          <div className="mt-3 flex items-center gap-2">
            <Input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="e.g. abc123de"
              maxLength={30}
              className={`h-8 flex-1 font-mono text-[12px] ${
                !draftValid ? 'border-destructive focus-visible:ring-destructive' : ''
              }`}
              data-testid="clarity-id-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && canSave) save(); }}
              aria-label="Microsoft Clarity Project ID"
            />
            <Button
              size="sm"
              onClick={save}
              disabled={saving || !canSave}
              className="h-8 gap-1 text-[11.5px]"
              data-testid="clarity-save-btn"
            >
              {saving
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
            {isActive && (
              <Button
                size="sm"
                variant="ghost"
                onClick={clear}
                disabled={clearing}
                className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                title="Remove Clarity Project ID"
                data-testid="clarity-clear-btn"
              >
                {clearing
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>

          {/* Inline validation hint */}
          {!draftValid && draft.trim() !== '' && (
            <p className="mt-1 text-[10.5px] text-destructive" data-testid="clarity-id-error">
              Project ID must be 1–30 letters, digits, underscores or hyphens (e.g.{' '}
              <code className="font-mono">abc123de</code>).
            </p>
          )}

          {/* Active ID display */}
          {isActive && (
            <p className="mt-1.5 text-[10.5px] text-muted-foreground" data-testid="clarity-active-id">
              Active ID:{' '}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10.5px] text-foreground">
                {current}
              </code>
            </p>
          )}
        </div>
      </div>

      {/* ── Privacy note ── */}
      <div className="mt-3 rounded-sm bg-muted/40 px-2.5 py-1.5 text-[10.5px] text-muted-foreground">
        <b>Scope.</b> Clarity fires <em>only</em> on your own Free Sample and Book Landing pages.
        Readers on other authors' pages are never tracked by your project. You can
        view your heatmaps and recordings at{' '}
        <a
          href="https://clarity.microsoft.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-primary hover:underline"
        >
          clarity.microsoft.com <ExternalLink className="h-2.5 w-2.5" />
        </a>{' '}
        any time. Readers are not notified individually; your site's privacy policy
        should mention session recording tools.
      </div>
    </div>
  );
}
